/*
 * Tailwind UI Component: Marketing > Page Sections > Footers > 4-column with newsletter dark
 * https://tailwindui.com/components/marketing/sections/footers#component-bca5fc82e894dfe85dc6ad982209c40e
 */

import * as React from 'react';
import { Link } from 'gatsby';
import Icon from '@components/utils/IconWrap';
import wwwApconCom from '../../www.apcon.com';
import Button from '../utils/Button';
import ButtonIcon from '../utils/ButtonIcon';

// Creates the year next to the copyright icon
const currentYear = new Date().getFullYear();

const footerContent = {
  title: {
    top: 'Questions?',
    bottom: "We've got answers.",
  },
  link: {
    text: 'Get in touch',
    url: wwwApconCom.contactUs,
    icon: <Icon icon="heroicons-solid:mail" />,
  },
  newsletter: {
    title: 'Subscribe to our newsletter',
    description: 'Get the latest news sent right to your inbox.',
  },

  hardware: {
    title: 'IntellaView Platform',
    links: [
      {
        id: 1,
        text: 'IntellaView Hardware',
        url: wwwApconCom.hardware.intellaview.DEFAULT,
        externalLink: false,
      },

      {
        id: 2,
        text: 'Network Taps',
        url: wwwApconCom.hardware.networkTaps.apconTap,
        externalLink: false,
      },
    ],
  },

  software: {
    title: 'Software',
    links: [
      {
        id: 1,
        text: 'IntellaView User Interface',
        url: wwwApconCom.software.intellaview,
        externalLink: false,
      },
      {
        id: 2,
        text: 'IntellaView Mobile',
        url: wwwApconCom.software.intellaviewMobile.DEFAULT,
        externalLink: false,
      },
      {
        id: 3,
        text: 'Enterprise',
        url: wwwApconCom.software.enterprise,
        externalLink: false,
      },
      {
        id: 4,
        text: 'IntellaTap-VM',
        url: wwwApconCom.software.intellatapVm,
        externalLink: false,
      },
    ],
  },

  resources: {
    title: 'Resources',
    links: [
      {
        id: 1,
        text: 'Support',
        url: 'https://portal.apcon.com/',
        externalLink: true,
      },
      {
        id: 2,
        text: 'Partners',
        url: wwwApconCom.channelPartners,
        externalLink: false,
      },
      {
        id: 3,
        text: 'Resources',
        url: wwwApconCom.search,
        externalLink: false,
      },
    ],
  },

  company: {
    title: 'Company',
    links: [
      {
        id: 1,
        text: 'Careers',
        url: wwwApconCom.careers,
        externalLink: false,
      },
      {
        id: 2,
        text: 'Blog',
        url: wwwApconCom.blog,
        externalLink: false,
      },
      {
        id: 3,
        text: 'News',
        url: wwwApconCom.newsroom,
        externalLink: false,
      },
    ],
  },
};

// Creates the underline effect when hovering over a link
const TextUnderline = ({ children }) => (
  <span className="bg-gradient-to-r from-white to-white duration-150 ease-linear transition-all bg-left-bottom bg-no-repeat bg-[length:0%_2px] hover:bg-[length:100%_2px]">
    {children}
  </span>
);

// Renders a <li> elements inside the Column component
const ListLink = props => {
  const { url, text, externalLink, endOfLife, endOfSale } = props;

  if (externalLink) {
    return (
      <li>
        <a
          href={url}
          className="text-base  leading-6 text-neutral-content hover:text-primary-content focus:text-primary-content focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-darkest focus:ring-info"
        >
          <div className="flex flex-row items-center">
            <TextUnderline>{text}</TextUnderline>
          </div>
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link
        to={url}
        className="text-base leading-6 text-neutral-content hover:text-primary-content focus:text-primary-content focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-darkest focus:ring-info"
      >
        <div className="flex flex-row items-center">
          <TextUnderline>
            <p>{text}</p>
            {endOfLife ||
              (endOfSale && (
                <p
                  className="ml-2 text-xs border border-error px-2 py-[.2rem] rounded-full text-error"
                  title={endOfLife ? 'End of Life' : 'End of Sale'}
                >
                  {endOfLife ? 'EOL' : 'EOS'}
                </p>
              ))}
          </TextUnderline>
        </div>
      </Link>
    </li>
  );
};

// Renders a list of links under a title
const Column = props => {
  const { title, links, marginTop } = props;

  return (
    <div className={`${marginTop ? 'mt-12 md:mt-0' : ''}`}>
      <h3 className="text-sm leading-5 font-semibold text-white uppercase">
        {title}
      </h3>
      <ul className="mt-4 space-y-4">
        {links.map(link => (
          <ListLink
            key={link.id}
            url={link.url}
            text={link.text}
            externalLink={link.externalLink}
            endOfLife={link.endOfLife}
            endOfSale={link.endOfSale}
          />
        ))}
      </ul>
    </div>
  );
};

// Display the OneTrust menu when clicked
const handleCookieSettingsClick = () => {
  if (
    typeof window !== 'undefined' &&
    window.OneTrust &&
    typeof window.OneTrust.ToggleInfoDisplay === 'function'
  ) {
    window.OneTrust.ToggleInfoDisplay();
  } else {
    console.warn("OneTrust functionality isn't available at the moment.");
  }
};

// Renders the social media icons and search icon
const IconLink = props => {
  const { url, name, icon } = props;

  return (
    <a
      href={url}
      target="_blank"
      className="text-primary-content hover:text-primary-content focus:text-primary-content focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-darkest focus:ring-info"
      title={name}
      rel="noreferrer"
    >
      <span className="sr-only">{name}</span>
      <div className="h-6 w-6 transition ease-in-out duration-150">
        <Icon icon={icon} />
      </div>
    </a>
  );
};

export default function Footer() {
  const { title, link, hardware, software, resources, company } = footerContent;

  return (
    <footer className="bg-primary-darkest relative z-40 pb-14 lg:pb-0">
      <h2 className="sr-only">Footer Menu</h2>
      <div className="container mx-auto text-center py-12 px-4 sm:px-6 lg:pt-24 lg:pb-8 lg:px-8">
        <h2 className="text-3xl leading-9 font-extrabold text-primary-content sm:text-3xl sm:leading-10">
          {title.top}
          <br />
          {title.bottom}
        </h2>
        <div className="mt-8 flex justify-center">
          <Button color="primary" base="xl" internalLinkTo={link.url}>
            <ButtonIcon icon={link.icon} base="xl" position="leading" />
            {link.text}
          </Button>
        </div>
        <div className="mt-16">
          <div
            className="flex flex-row justify-center"
            aria-label="footer navigation"
          >
            <div className="flex flex-row justify-between w-full max-w-4xl gap-16">
              <Column
                title={hardware.title}
                links={[...hardware.links, ...software.links]}
              />
              <Column title={resources.title} links={resources.links} />
              <Column title={company.title} links={company.links} marginTop />
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
        <div className="mt-8 py-8 flex justify-center">
          <Button
            color="primary"
            base="xl"
            internalLinkTo={wwwApconCom.careers}
          >
            We&apos;re Hiring
          </Button>
        </div>
        <div className="pt-8 border-t border-neutral md:flex md:items-center md:justify-between">
          <Link
            title="Search"
            to={wwwApconCom.search}
            className="text-neutral-content hover:text-primary-content focus:text-primary-content focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-darkest focus:ring-info order-1 hidden md:block md:mr-40"
          >
            <span className="sr-only">Search</span>
            <div className="h-6 w-6 transition ease-in-out duration-150">
              <Icon icon="heroicons-outline:search" />
            </div>
          </Link>
          <div className="flex space-x-6 md:order-3">
            <Link
              title="Search"
              to={wwwApconCom.search}
              className="text-neutral-content hover:text-primary-content focus:text-primary-content focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-darkest focus:ring-info md:hidden mr-auto"
            >
              <span className="sr-only">Search</span>
              <div className="h-6 w-6 transition ease-in-out duration-150">
                <Icon icon="heroicons-outline:search" />
              </div>
            </Link>
            <IconLink
              url={wwwApconCom.social.youtube}
              name="YouTube"
              icon="mdi:youtube"
            />
            <IconLink
              url={wwwApconCom.social.linkedin}
              name="LinkedIn"
              icon="mdi:linkedin"
            />
            <IconLink
              url={wwwApconCom.social.facebook}
              name="Facebook"
              icon="mdi:facebook"
            />
            <IconLink
              url={wwwApconCom.social.twitter}
              name="Twitter"
              icon="mdi:twitter"
            />
          </div>
          <div className="mt-8 text-neutral-content md:mt-0 md:order-2 md:pl-6">
            <p className="text-sm leading-6">
              &copy; {currentYear} APCON, Inc. All rights reserved.
            </p>
            <div className="flex ">
              <Link
                to={wwwApconCom.privacyPolicy}
                className="text-xs hover:text-primary-content focus:text-primary-content focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-darkest focus:ring-info"
              >
                <TextUnderline>Privacy Policy</TextUnderline>
              </Link>
              <button
                id="ot-sdk-btn"
                onClick={handleCookieSettingsClick}
                className="ml-4 text-xs hover:text-primary-content focus:text-primary-content focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-darkest focus:ring-info"
              >
                <TextUnderline>Cookie Settings</TextUnderline>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
